import { useState, useEffect, useContext, useMemo } from "react";
import dayjs from "dayjs";

import { makeStyles } from "@mui/styles";
import Dialog from "@mui/material/Dialog";

import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import DataUsageOutlinedIcon from "@mui/icons-material/DataUsageOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { IconButton } from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";

// My Files
import { DatabaseContext } from "../../../../context";
import HubUsageDateFilterDialog from "./filters/HubUsageDateFilterDialog";
import HubUsageFileTypeFilterDialog from "./filters/HubUsageFileTypeFilterDialog";
import HubUsageFileFilterDialog from "./filters/HubUsageFileFilterDialog";
import { downloadBlob } from "../../../../utility";

const useStyles = makeStyles((theme) => ({
    root: {
        textAlign: "center",
        alignItems: "center",
    },
    toolbarTitle: {
        // flexGrow: 1,
    },
    title: {
        marginTop: 15,
    },
    tableContainer: {
        marginTop: 15,
        marginBottom: 10,
        maxHeight: window.innerHeight - 255,
        width: window.innerWidth,
        overflow: "scroll",
    },
    tableHead: {
        background: theme.palette.primary.main,
    },
    tableHeaderCell: {
        // The head row sits on primary.main in both palettes, so white ink and a
        // palette divider stay legible either way.
        color: "white",
        borderRight: `1px solid ${theme.palette.divider}`,
    },
    loadingContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: 400,
        gap: 20,
    },
}));

export default function HubFileUsageDialog(props) {
    const classes = useStyles();
    const { getFileUsage, files, groups } = useContext(DatabaseContext);

    const [fileTypeFilterDialogIsOpen, setFileTypeFilterDialogIsOpen] =
        useState(false);
    const [fileFilterDialogIsOpen, setFileFilterDialogIsOpen] = useState(false);
    const [dateFilterDialogIsOpen, setDateFilterDialogIsOpen] = useState(false);

    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadedFileUsage, setLoadedFileUsage] = useState(null);
    const [loadedHubKey, setLoadedHubKey] = useState(null);
    const [fileSearch, setFileSearch] = useState("");

    const hubKey = props.hub ? props.hub.key : null;

    const [filterStartDate, setFilterStartDate] = useState(null);
    const [filterEndDate, setFilterEndDate] = useState(null);

    const [excludedFileKeysFromFileFilter, setExcludedFileKeysFromFileFilter] =
        useState([]);

    const [hubFiles, setHubFiles] = useState([]);

    const [includeVideo, setIncludeVideo] = useState(true);
    const [includeAudio, setIncludeAudio] = useState(true);
    const [includeImages, setIncludeImages] = useState(true);
    const [includePDFs, setIncludePDFs] = useState(true);
    const [includePresentations, setIncludePresentations] = useState(true);
    const [includeDocuments, setIncludeDocuments] = useState(true);

    const [filterIsActive, setFilterIsActive] = useState(false);

    // Create group lookup map for better performance
    const groupMap = useMemo(() => {
        const map = new Map();
        groups.forEach((group) => {
            map.set(group.key, group);
        });
        return map;
    }, [groups]);

    useEffect(() => {
        let isActive = true;

        if (!props.open || !hubKey) {
            setIsLoading(false);
            setLoadedFileUsage(null);
            setLoadedHubKey(null);
            setRows([]);
            return () => {
                isActive = false;
            };
        }

        setIsLoading(true);
        setLoadedFileUsage(null);
        setLoadedHubKey(null);
        setRows([]);

        getFileUsage(true).then((usageData) => {
            if (!isActive) return;

            setLoadedFileUsage(usageData);
            setLoadedHubKey(hubKey);
        });

        return () => {
            isActive = false;
        };
    }, [props.open, hubKey, getFileUsage]);

    useEffect(() => {
        if (
            !props.hub ||
            !props.open ||
            loadedFileUsage === null ||
            loadedHubKey !== props.hub.key
        ) {
            return;
        }

        const compileData = () => {
            setIsLoading(true);

            // Use setTimeout to allow UI to update with loading indicator
            return setTimeout(() => {
                try {
                    let hubFilesArray = files.filter((f) =>
                        f.ancestors.includes(props.hub.key)
                    );

                    // Set the File category
                    hubFilesArray.forEach((file) => {
                        if (file.type.includes("video")) {
                            file.category = "Video";
                        } else if (file.type.includes("audio")) {
                            file.category = "Audio";
                        } else if (file.type.includes("image")) {
                            file.category = "Image";
                        } else if (file.type.includes("pdf")) {
                            file.category = "PDF";
                        } else if (file.type.includes("presentation")) {
                            file.category = "Presentation";
                        } else {
                            let extension =
                                file.filename.substring(
                                    file.filename.lastIndexOf(".") + 1,
                                    file.filename.length
                                ) || file.filename;

                            if (extension === "key") {
                                file.category = "Presentation";
                            } else {
                                file.category = "Document";
                            }
                        }
                    });

                    setHubFiles(hubFilesArray);

                    // Build usage map for faster lookups: Map<fileKey, usage[]>
                    const usageMap = new Map();
                    loadedFileUsage.forEach((usage) => {
                        if (!usageMap.has(usage.file)) {
                            usageMap.set(usage.file, []);
                        }
                        usageMap.get(usage.file).push(usage);
                    });

                    // Create row data for the files that are included after filtering
                    let rowDataArray = [];

                    hubFilesArray.forEach((file) => {
                        // Check if file should be excluded based on filters
                        if (excludedFileKeysFromFileFilter.includes(file.key)) {
                            return;
                        }
                        if (file.category === "Video" && !includeVideo) {
                            return;
                        }
                        if (file.category === "Audio" && !includeAudio) {
                            return;
                        }
                        if (file.category === "Image" && !includeImages) {
                            return;
                        }
                        if (file.category === "PDF" && !includePDFs) {
                            return;
                        }
                        if (
                            file.category === "Presentation" &&
                            !includePresentations
                        ) {
                            return;
                        }
                        if (file.category === "Document" && !includeDocuments) {
                            return;
                        }

                        const matchingFileUsages = usageMap.get(file.key) || [];

                        var viewCount = 0;
                        var downloadCount = 0;
                        var timelineCount = 0;
                        var playlistCount = 0;
                        var playlistShareCount = 0;
                        var mostRecentDate = null;

                        var userKeysViewed = new Set();

                        matchingFileUsages.forEach((usage) => {
                            const usageDate = dayjs(usage.date);

                            // Date filtering
                            if (
                                filterStartDate &&
                                usageDate.isBefore(
                                    dayjs(filterStartDate),
                                    "day"
                                )
                            ) {
                                return;
                            }
                            if (
                                filterEndDate &&
                                usageDate.isAfter(dayjs(filterEndDate), "day")
                            ) {
                                return;
                            }

                            switch (usage.action) {
                                case "Open":
                                    viewCount += 1;
                                    userKeysViewed.add(usage.user);

                                    if (
                                        mostRecentDate === null ||
                                        usageDate.isAfter(mostRecentDate)
                                    ) {
                                        mostRecentDate = usageDate;
                                    }
                                    break;

                                case "Download":
                                    downloadCount += 1;
                                    break;

                                case "TimelineCreate":
                                    timelineCount += 1;
                                    break;

                                case "PlaylistCreate":
                                    playlistCount += 1;
                                    break;

                                case "PlaylistShare":
                                    var userKeysSharedTo = [
                                        ...usage.usersSharedWith,
                                    ];

                                    usage.groupsSharedWith.forEach(
                                        (groupRef) => {
                                            const groupObject = groupMap.get(
                                                groupRef.key
                                            );
                                            if (groupObject) {
                                                groupObject.users.forEach(
                                                    (groupUser) => {
                                                        userKeysSharedTo.push(
                                                            groupUser.key
                                                        );
                                                    }
                                                );
                                            }
                                        }
                                    );

                                    playlistShareCount +=
                                        userKeysSharedTo.length;
                                    break;

                                default:
                                    break;
                            }
                        });

                        // Create the row
                        rowDataArray.push({
                            key: file.key,
                            name: file.displayName,
                            lastViewed: mostRecentDate,
                            viewCount: viewCount,
                            usersViewedCount: userKeysViewed.size,
                            downloadCount: downloadCount,
                            timelineCount: timelineCount,
                            playlistCount: playlistCount,
                            playlistShareCount: playlistShareCount,
                        });
                    });

                    rowDataArray.sort((a, b) => b.viewCount - a.viewCount);

                    setRows(rowDataArray);
                } finally {
                    setIsLoading(false);
                }
            }, 0);
        };

        const compileTimeout = compileData();
        return () => clearTimeout(compileTimeout);
        // eslint-disable-next-line
    }, [
        props.hub,
        props.open,
        loadedFileUsage,
        loadedHubKey,
        filterStartDate,
        filterEndDate,
        excludedFileKeysFromFileFilter,
        includeVideo,
        includeAudio,
        includeImages,
        includePDFs,
        includePresentations,
        includeDocuments,
    ]);

    function handleFileTypeFilterClick() {
        setFileTypeFilterDialogIsOpen(true);
    }

    function handleDownloadPress() {
        var csvString =
            "File, Last Viewed, Num Views, Num Users Viewed, Downloads, Timelines Created, Playlists Created, Playlists Shared\n";

        filteredRows.forEach((row) => {
            csvString += row.name.replace(",", "") + ", ";
            csvString +=
                (row.lastViewed
                    ? dayjs(row.lastViewed).toString()
                    : "-"
                ).replace(",", "") + ", ";
            csvString += row.viewCount + ", ";
            csvString += row.usersViewedCount + ", ";
            csvString += row.downloadCount + ", ";
            csvString += row.timelineCount + ", ";
            csvString += row.playlistCount + ", ";
            csvString += row.playlistShareCount + "\n";
        });

        downloadBlob(csvString, "export.csv", "text/csv;charset=utf-8;");
    }

    function handleCloseFileTypeFilterDialog(
        video,
        audio,
        images,
        pdfs,
        presentations,
        documents
    ) {
        setIncludeVideo(video);
        setIncludeAudio(audio);
        setIncludeImages(images);
        setIncludePDFs(pdfs);
        setIncludePresentations(presentations);
        setIncludeDocuments(documents);

        setFileTypeFilterDialogIsOpen(false);
        setFilterIsActive(true);
    }

    function handleFileFilterClick() {
        setFileFilterDialogIsOpen(true);
    }

    function handleCloseFileFilterDialog(newExcludedFileKeys) {
        setExcludedFileKeysFromFileFilter(newExcludedFileKeys);
        setFileFilterDialogIsOpen(false);
        setFilterIsActive(true);
    }

    function handleDateFilterClick() {
        setDateFilterDialogIsOpen(true);
    }

    function handleCloseDateFilterDialog(startDate, endDate) {
        setFilterStartDate(startDate);
        setFilterEndDate(endDate);
        setDateFilterDialogIsOpen(false);

        // Clearing the dates is not a filter, so it must not switch the
        // "Clear filters" button on by itself.
        if (startDate && endDate) {
            setFilterIsActive(true);
        }
    }

    function handleClearFiltersClick() {
        setFilterStartDate(null);
        setFilterEndDate(null);
        setExcludedFileKeysFromFileFilter([]);
        setIncludeVideo(true);
        setIncludeAudio(true);
        setIncludeImages(true);
        setIncludePDFs(true);
        setIncludePresentations(true);
        setIncludeDocuments(true);
        setFilterIsActive(false);
    }

    const handleClose = () => {
        props.onClose();
    };

    const filteredRows = useMemo(() => {
        const searchTerm = fileSearch.trim().toLocaleLowerCase();

        if (!searchTerm) return rows;

        return rows.filter((row) =>
            row.name.toLocaleLowerCase().includes(searchTerm)
        );
    }, [fileSearch, rows]);

    const showLoading =
        isLoading || loadedFileUsage === null || loadedHubKey !== hubKey;

    return (
        <>
            <Dialog
                onClose={handleClose}
                aria-labelledby="hub-file-usage-dialog-title"
                open={props.open}
                fullWidth
                maxWidth="lg"
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            overflow: "hidden",
                            maxHeight: {
                                xs: "calc(100% - 16px)",
                                sm: "calc(100% - 48px)",
                            },
                            m: { xs: 1, sm: 3 },
                            width: {
                                xs: "calc(100% - 16px)",
                                sm: "calc(100% - 48px)",
                            },
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        px: { xs: 2, sm: 3 },
                        py: 2.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            color: "primary.main",
                            backgroundColor: "action.hover",
                        }}
                    >
                        <DataUsageOutlinedIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            id="hub-file-usage-dialog-title"
                            variant="h5"
                            component="h2"
                            sx={{ lineHeight: 1.1 }}
                        >
                            Hub file usage
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                        >
                            {props.hub?.name}
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="Close hub file usage"
                        onClick={handleClose}
                        sx={{ mt: -0.75, mr: -1 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent
                    dividers
                    sx={{
                        p: { xs: 1.5, sm: 2.5 },
                        backgroundColor: "background.default",
                    }}
                >
                    {!showLoading && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: {
                                    xs: "stretch",
                                    md: "center",
                                },
                                justifyContent: "space-between",
                                flexDirection: {
                                    xs: "column",
                                    md: "row",
                                },
                                gap: 1.5,
                                mb: 1.5,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={handleFileTypeFilterClick}
                                >
                                    File types
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleFileFilterClick}
                                >
                                    Files
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleDateFilterClick}
                                >
                                    Dates
                                </Button>
                                {filterIsActive && (
                                    <Button
                                        color="error"
                                        onClick={handleClearFiltersClick}
                                    >
                                        Clear filters
                                    </Button>
                                )}
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: { xs: "stretch", sm: "center" },
                                    flexDirection: { xs: "column", sm: "row" },
                                    gap: 1,
                                }}
                            >
                                <TextField
                                    label="Search files"
                                    placeholder="Type a file name"
                                    size="small"
                                    value={fileSearch}
                                    onChange={(event) =>
                                        setFileSearch(event.target.value)
                                    }
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon color="action" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: fileSearch ? (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        edge="end"
                                                        size="small"
                                                        aria-label="Clear file search"
                                                        onClick={() =>
                                                            setFileSearch("")
                                                        }
                                                    >
                                                        <ClearIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            ) : null,
                                        },
                                    }}
                                    sx={{ minWidth: { sm: 240 } }}
                                />
                                <Button
                                    onClick={handleDownloadPress}
                                    variant="outlined"
                                    startIcon={<DownloadOutlinedIcon />}
                                    sx={{ flexShrink: 0 }}
                                >
                                    Download CSV
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {showLoading ? (
                        <Box
                            className={classes.loadingContainer}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2.5,
                                backgroundColor: "background.paper",
                            }}
                        >
                            <CircularProgress size={60} />
                            <Typography variant="h6">
                                Loading and compiling usage data...
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                                maxHeight: {
                                    xs: "calc(100vh - 280px)",
                                    sm: "calc(100vh - 330px)",
                                },
                                overflow: "auto",
                                borderRadius: 2.5,
                                border: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <Table
                                stickyHeader
                                size="small"
                                aria-label="Hub file usage"
                                sx={{
                                    minWidth: 1050,
                                    "& .MuiTableCell-root": {
                                        px: { xs: 1.5, sm: 2 },
                                        py: 1.25,
                                    },
                                }}
                            >
                                <TableHead
                                    sx={{
                                        "& .MuiTableCell-root": {
                                            fontWeight: 800,
                                            backgroundColor:
                                                "background.subtle",
                                        },
                                    }}
                                >
                                    <TableRow>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                            align="center"
                                        >
                                            File
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                            align="center"
                                        >
                                            Last Viewed
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                            align="center"
                                        >
                                            Num Views
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                            align="center"
                                        >
                                            Num Users Viewed
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                            align="center"
                                        >
                                            Downloads
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                            align="center"
                                        >
                                            Timelines Created
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                            align="center"
                                        >
                                            Playlists Created
                                        </TableCell>
                                        <TableCell
                                            className={classes.tableHeaderCell}
                                            align="center"
                                        >
                                            Playlists Shared
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredRows.length === 0 && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={8}
                                                align="center"
                                                sx={{ py: 5 }}
                                            >
                                                <Typography color="text.secondary">
                                                    No files match your search.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {filteredRows.map((row) => (
                                        <TableRow
                                            key={row.key}
                                            style={{
                                                cursor: "pointer",
                                            }}
                                        >
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                align="center"
                                                sx={{
                                                    borderRight: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                {row.name}
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                align="center"
                                                sx={{
                                                    borderRight: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                {row.lastViewed
                                                    ? dayjs(
                                                          row.lastViewed
                                                      ).toString()
                                                    : "-"}
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                align="center"
                                                sx={{
                                                    borderRight: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                {row.viewCount}
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                align="center"
                                                sx={{
                                                    borderRight: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                {row.usersViewedCount}
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                align="center"
                                                sx={{
                                                    borderRight: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                {row.downloadCount}
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                align="center"
                                                sx={{
                                                    borderRight: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                {row.timelineCount}
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                align="center"
                                                sx={{
                                                    borderRight: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                {row.playlistCount}
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                align="center"
                                                sx={{
                                                    borderRight: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                {row.playlistShareCount}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
                    <Button
                        onClick={handleClose}
                        variant="contained"
                        disableElevation
                    >
                        Close
                    </Button>
                </DialogActions>
                <HubUsageFileTypeFilterDialog
                    open={fileTypeFilterDialogIsOpen}
                    onClose={handleCloseFileTypeFilterDialog}
                    includeVideo={includeVideo}
                    includeAudio={includeAudio}
                    includeImages={includeImages}
                    includePDFs={includePDFs}
                    includePresentations={includePresentations}
                    includeDocuments={includeDocuments}
                />
                <HubUsageFileFilterDialog
                    open={fileFilterDialogIsOpen}
                    onClose={handleCloseFileFilterDialog}
                    files={hubFiles}
                    excludedFileKeys={excludedFileKeysFromFileFilter}
                />
                <HubUsageDateFilterDialog
                    open={dateFilterDialogIsOpen}
                    onClose={handleCloseDateFilterDialog}
                    startDate={filterStartDate}
                    endDate={filterEndDate}
                />
            </Dialog>
        </>
    );
}
